import type { ValueOf } from 'type-fest';

/**
 * Message type constants shared across satellite-rachel-web and satellite-landlord-web
 * Single source of truth for all message type classifications
 */
export const MESSAGE_TYPES = {
  HIGH_PRIORITY: 'highPriority',
  HIGH_PRIORITY_WITH_LOGO: 'highPriorityWithLogo',
  LOW_PRIORITY: 'lowPriority',
  PHONE_LEAD: 'phoneLead',
  LISTING_MESSAGE: 'listingMessage',
  RENTER_PROFILE_REQUEST: 'renterProfileRequest',
  RENTER_PROFILE_SUBMISSION: 'renterProfileSubmission',
  SYSTEM_MESSAGE: {
    APPLICATIONS: {
      APPLICATION_APPROVED_OFF_PLATFORM: 'applicationApprovedOffPlatform',
      APPLICATION_DECLINED: 'applicationDeclined',
      APPLICATION_INVITATION: 'applicationInvitation',
      APPLICATION_OPENED: 'applicationOpened',
      APPLICATION_REQUEST: 'applicationRequest',
      APPLICATION_UNREAD: 'applicationUnread',
      APPLICATION_RECEIVED: 'applicationReceived',
      APPLICATION_WITHDRAWN: 'applicationWithdrawn',
    },
    FAILED_TO_LOAD: 'failedToLoad',
    PAYMENTS: {
      IRENE_PAYMENT_MARKED_AS_PAID: 'irenePaymentMarkedAsPaid',
      IRENE_RENT_DEPOSIT_CONFIRMATION: 'ireneRentDepositConfirmation',
      PAYMENTS_LANDLORD_ENDED_LEASE: 'landlordEndedLease',
      PAYMENTS_LANDLORD_EXTENDED_LEASE: 'landlordExtendedLease',
      PAYMENTS_LANDLORD_LEASE_TO_M2M: 'landlordLeaseToM2M',
      PAYMENTS_LANDLORD_PROPERTY_PAYOUT_PAID: 'landlordPropertyPayoutPaid',
      PAYMENTS_LANDLORD_PROPERTY_RENT_PAID: 'landlordPropertyRentPaid',
      PAYMENTS_LANDLORD_REQUESTED_RENT_AMOUNT_CHANGE: 'landlordRequestedRentAmountChange',
      PAYMENTS_LANDLORD_SHORTENED_LEASE: 'landlordShortenedLease',
      PAYMENTS_OTP_REQUEST_PAID: 'landlordPropertyOtpPaid',
      PAYMENTS_OTP_REQUEST_SENT: 'landlordSentOtpRequest',
      PAYMENTS_PAYOUT_CREATED: 'landlordPropertyPayoutPaid',
      PAYMENTS_PAYOUT_DELAYED: 'landlordPropertyPayoutDelayed',
      PAYMENTS_RENT_AMOUNT_CHANGED_FOR_PROPERTY: 'landlordPropertyRentAmountChanged',
      PAYMENTS_RENT_PAID_OFFLINE: 'landlordPropertyRentPaidOffline',
      PAYMENTS_RENTER_RENT_OVERDUE: 'landlordPropertyRentOverdue',
      PAYMENTS_RENTER_PAYMENT_COMPLETE: 'renterRentPaymentComplete',
      PAYMENTS_RENTER_PAYMENT_ENDING: 'renterPaymentEnding',
      PAYMENTS_RENTER_PAYMENT_FAILED: 'renterPaymentFailed',
      PAYMENTS_RENTER_PAYMENT_INVITE: 'renterPaymentInvite',
      PAYMENTS_RENTER_SCHEDULED_FIRST_PAYMENT: 'renterScheduledFirstRentPayment',
    },
    LEASES: {
      LEASE_SENT_FOR_OFFLINE_SIGNING: 'leaseSentForOfflineSigning',
      LEASE_SENT_FOR_ONLINE_SIGNING: 'leaseSentForOnlineSigning',
      LEASE_SIGNING_CANCELLED: 'leaseSigningCancelled',
      LEASE_SIGNED_BY_TENANTS_SOME_REMAINING: 'leaseSignedByTenantSomeRemaining',
      LEASE_SIGNED_BY_LAST_TENANT: 'leaseSignedByLastTenant',
      LEASE_COUNTERSIGNING_COMPLETE: 'leaseCountersigningComplete',
      LEASE_ACTIVE: 'leaseActive',
      LEASE_EXPIRING_SOON: 'leaseExpiringSoon',
      LEASE_EXPIRED: 'leaseExpired',
    },
    TOURS: {
      INSTANT_TOUR_SCHEDULED: 'instantTourScheduled',
      INSTANT_TOUR_RESCHEDULED: 'instantTourRescheduled',
      INSTANT_TOUR_CANCELED: 'instantTourCanceled',
      TOUR_INSTANT_BOOKED: 'tourInstantBooked',
      TOUR_REQUEST_QUICK: 'tourRequestQuick',
      TOUR_CONFIRMED: 'tourConfirmed',
      TOUR_DECLINED: 'tourDeclined',
      TOUR_CANCELED: 'tourCanceled',
      TOUR_CANCELED_BY_RENTER: 'tourCanceledByRenter',
      TOUR_REQUEST_CANCELED_BY_RENTER: 'tourRequestCanceledByRenter',
      TOUR_RESCHEDULED_INSTANT: 'tourRescheduledInstant',
      TOUR_RESCHEDULED_REVIEW: 'tourRescheduledReview',
      TOUR_RESCHEDULED_CONFIRMED: 'tourRescheduledConfirmed',
      TOUR_RESCHEDULED_BY_LANDLORD: 'tourRescheduledByLandlord',
      TOUR_REQUEST_EXPIRED: 'tourRequestExpired',
    },
  },
} as const;

export type MessageType = ValueOf<typeof MESSAGE_TYPES>;
